import { toURL } from 'lib/strings'

// NOTE: ported from global-exceptions in mobile-frontend

// Indicates that the error was already handled and reported by our graphql integration. No need
// to report it again
function isDoNotReport(bugsnagError, originalError) {
  return originalError?.doNotReport || bugsnagError?.doNotReport
}

// We don't really care if local network errors occurs. Our app needs to gracefully handle it, but
// we definitely don't want these errors reported to bugsnag
function isNetworkRequestFailed(bugsnagError) {
  if (!bugsnagError) return false
  return bugsnagError.errorMessage.match('Network request failed')
}

// Not a critical failure and can be safely ignored
// https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
// https://stackoverflow.com/questions/64238740/how-to-ignore-the-resizeobserver-loop-limit-exceeded-in-testcafe
function isResizeObserverLimitError(bugsnagError) {
  return [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
  ].includes(bugsnagError?.errorMessage)
}

// If instead of a descriptive error message we get a general
// 'Script error.', it means error occurred in a script with
// different origin (e.g. in an iframe created by an editor),
// i.e. it's not a problem with our code.
//
// In such cases it's better to assume that that code is able to
// continue to work in presence of errors rather than kill the
// whole app — we don't have enough information to effectively
// debug the problem anyway.
//
// More info:
//   https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror#Notes
function isScriptError(bugsnagError) {
  if (!bugsnagError) return false
  return bugsnagError.errorMessage.match('Script error.')
}

// If the executing file is not from Groove
// Executing file is from Groove if any of the stacktrace files has groovehq domain
// this is to prevent us reporting errors from other js outside of widget code
// https://docs.bugsnag.com/platforms/javascript/react/customizing-error-reports/#errors
function isExecutingFileOutsideGroove(bugsnagError) {
  const grooveRegex = /groovehq/
  const stack = bugsnagError?.stacktrace || []
  const idx = stack.findIndex(st => grooveRegex.test(st?.file || ''))

  if (stack.length > 1 && idx === stack.length - 1) {
    // only the bottom stack is groove url.
    // There are situations where the stack above it is from another domain.
    const url = toURL(stack[idx - 1]?.file || '')

    // not a url, assume it's from groovehq
    if (!url) return false

    // file is a url, check if hostname is groovehq
    return !grooveRegex.test(url.hostname)
  }

  return idx === -1
}

export function isIgnoredError(bugsnagError, originalError) {
  // https://docs.bugsnag.com/platforms/javascript/customizing-error-reports/#errors
  return [
    isScriptError,
    isNetworkRequestFailed,
    isDoNotReport,
    isResizeObserverLimitError,
    isExecutingFileOutsideGroove,
  ].some(isError => isError(bugsnagError, originalError))
}
